import { Fragment, FC } from 'react';
import { Alert, Text } from 'roo-ui/components';
import { Link } from 'components/Typography';
import { FormattedDate } from 'components/Formatters';
import CancellationPolicyModal from 'components/Modals/CancellationPolicyModal';
import { DetailsBooking } from 'types';
import { useDetailsBooking, useRoutableModal } from 'lib/hooks';
import * as track from 'lib/gaTracking/crossPageEvents';

const getAlertConfig = (booking: DetailsBooking) => {
  const activeCancellationWindow = booking.reservation.cancellationPolicy?.activeCancellationWindow;

  if (!activeCancellationWindow) {
    return null;
  }

  if (activeCancellationWindow.isNonRefundable) {
    return {
      alertVariant: 'error',
      message: `This property's cancellation policy is non-refundable`,
    };
  }

  if (activeCancellationWindow.isFullyRefundable) {
    return {
      alertVariant: 'success',
      message: (
        <Fragment>
          Your booking is fully refundable until <FormattedDate date={activeCancellationWindow.endTime} />
        </Fragment>
      ),
    };
  }

  if (activeCancellationWindow.isPartiallyRefundable) {
    return {
      alertVariant: 'error',
      message: `The property's cancellation policy allows for a partial refund`,
    };
  }
};

const CancellationPolicyAlert: FC<{ [key: string]: any }> = ({ ...alertProps }) => {
  const { booking } = useDetailsBooking();
  const { RoutableModalWrapper, ...modal } = useRoutableModal('cancellation-policy', {
    onCloseIteraction: track.cancellationPolicyPopUpCloseButtonSelected,
  });

  const alertConfig = getAlertConfig(booking);

  if (!alertConfig) {
    return null;
  }

  return (
    <Alert variant={alertConfig.alertVariant} {...alertProps}>
      <Text fontSize="lg" fontWeight="bold" mr={4}>
        {alertConfig.message}
      </Text>
      <Link to={modal.path} onClick={track.cancellationPolicyPopUpLinkSelected}>
        View hotel cancellation policy
      </Link>
      <RoutableModalWrapper>
        <CancellationPolicyModal {...modal.props} rawDescription={booking.reservation.cancellationPolicy?.description ?? ''} />
      </RoutableModalWrapper>
    </Alert>
  );
};

export default CancellationPolicyAlert;
